import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Collapse, Stack } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import RachelSparkIcon from '../RachelSparkIcon/RachelSparkIcon';
import { getShimmerTextSx } from '../shimmerText';
import RachelReasoningTimeline from './components/RachelReasoningTimeline/RachelReasoningTimeline';

export type RachelReasoningStepState = 'ok' | 'now' | 'todo';

export interface RachelReasoningStep {
  id: string;
  text: string;
  sub?: string;
  state: RachelReasoningStepState;
}

interface RachelReasoningTraceProps {
  steps: RachelReasoningStep[];
  isLive: boolean;
}

const RachelReasoningTrace = ({ steps, isLive }: RachelReasoningTraceProps) => {
  const { translate } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (steps.length === 0) {
    return null;
  }

  const toggleExpanded = () => setIsExpanded((previous) => !previous);

  return (
    <Box>
      <Stack
        component="button"
        type="button"
        aria-expanded={isExpanded}
        direction="row"
        alignItems="center"
        gap={1}
        onClick={toggleExpanded}
        sx={{
          cursor: 'pointer',
          width: '100%',
          padding: 0,
          border: 'none',
          background: 'none',
          textAlign: 'left',
          color: 'inherit',
          font: 'inherit',
        }}
      >
        {isLive && <RachelSparkIcon size={16} isPulsing />}
        {isLive ? (
          <Typography
            variant="body2"
            sx={(theme) => ({
              flex: 1,
              ...getShimmerTextSx(theme, { start: 30, middle: 50, end: 70 }),
            })}
          >
            {translate(LangEnum.RACHEL_REASONING_LIVE)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {translate(LangEnum.RACHEL_REASONING_LABEL)}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            color: 'text.secondary',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          <Icon icon={ChevronDown} size="xs" />
        </Box>
      </Stack>

      <Collapse in={isExpanded}>
        <Box sx={{ paddingY: 1, pl: 1 }}>
          <RachelReasoningTimeline steps={steps} isDone={!isLive} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default RachelReasoningTrace;
